// TypeSelector — combo input + popover for any inventory_type_* enum. The
// trigger IS the input. Typing filters the canonical list AND primes a
// "use as custom" affordance. Org's prior raw values appear in their own
// "Custom" section so they're re-usable before the canonical agent reviews
// them. Ported from
// private/nos-housekeeping-features/project/components-standalone/type-selector.jsx
// (TypeSelector at line 140-447).

import * as React from "react";
import { createPortal } from "react-dom";
import type { RawEntry, TypeCatalogRow } from "./_types";
import { useClickOutside, usePopoverPosition } from "./usePopover";

export type TypeValue = { id?: string; raw?: string };

type Props = {
  value: TypeValue;
  onChange: (next: TypeValue) => void;
  catalog: TypeCatalogRow[];
  rawEntries?: RawEntry[];
  /** Property name to group canonical rows by inside the popover (e.g.
   *  "category" for units, "state" for forms). */
  groupBy?: keyof TypeCatalogRow | ((row: TypeCatalogRow) => string);
  renderLabel?: (row: TypeCatalogRow) => React.ReactNode;
  renderHint?: (row: TypeCatalogRow) => React.ReactNode;
  /** What the input shows when closed. Defaults to row.code or row.raw. */
  closedText?: (row: TypeCatalogRow, value: TypeValue) => string;
  /** Overrides the WHOLE closed trigger with a custom node (e.g. a chip).
   *  The input still shows on focus. */
  renderTrigger?: (row: TypeCatalogRow | null, value: TypeValue) => React.ReactNode;
  prefix?: React.ReactNode;
  inputAlign?: "left" | "center" | "right";
  placeholder?: string;
  searchPlaceholder?: string;
  popoverMinWidth?: number | null;
  popoverAlign?: "start" | "end";
  /** Hard cap on the popover's height in px. Defaults to 360. */
  popoverMaxHeight?: number;
  ariaLabel?: string;
};

const CaretDown = ({ size = 11 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const Check = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/** Small grey badge attached to user-typed (uncanonical) values. */
export function RawBadge({ small = false }: { small?: boolean }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: small ? "1px 5px" : "2px 6px",
        borderRadius: 3,
        background: "var(--bg-sunk)",
        color: "var(--ink-3)",
        border: "1px solid var(--line)",
        fontFamily: "var(--font-mono)",
        fontSize: small ? 8.5 : 9.5,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        lineHeight: 1,
      }}
    >
      custom
    </span>
  );
}

function SectionHead({
  title,
  count,
  kind,
}: {
  title: string;
  count: number;
  kind: "canon" | "raw";
}) {
  return (
    <div
      style={{
        padding: "8px 12px 5px",
        background:
          kind === "raw" ? "rgba(231, 201, 122, 0.08)" : "var(--panel)",
        borderBottom: "1px solid var(--line-2)",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <span
        className="mono"
        style={{
          fontSize: 9,
          color: kind === "raw" ? "#7c5813" : "var(--ink-3)",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          fontWeight: 700,
        }}
      >
        {title}
      </span>
      <span
        className="mono"
        style={{
          fontSize: 9,
          color: "var(--ink-4)",
          padding: "1px 5px",
          borderRadius: 3,
          background: "var(--bg-sunk)",
          border: "1px solid var(--line)",
          fontWeight: 600,
        }}
      >
        {count}
      </span>
    </div>
  );
}

export function TypeSelector({
  value,
  onChange,
  catalog,
  rawEntries = [],
  groupBy,
  renderLabel,
  renderHint,
  closedText,
  renderTrigger,
  prefix,
  inputAlign = "left",
  placeholder = "Select…",
  searchPlaceholder = "Search, or type a new value…",
  popoverMinWidth = null,
  popoverAlign = "start",
  popoverMaxHeight = 360,
  ariaLabel,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [focused, setFocused] = React.useState(false);
  // True once the user types into the input (or clears it). Until then,
  // the input mirrors the closed display value but the dropdown is
  // unfiltered.
  const [userEdited, setUserEdited] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);
  const popRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // On close (click outside / Enter / Escape), auto-commit the typed query
  // as a raw value if the user actually edited and it doesn't match anything.
  const closeAndAutoCommit = React.useCallback(() => {
    if (userEdited) {
      const trimmed = q.trim();
      if (trimmed) {
        const exact = catalog.find(
          (r) =>
            (r.code || "").toLowerCase() === trimmed.toLowerCase() ||
            (r.label || "").toLowerCase() === trimmed.toLowerCase(),
        );
        const existing = rawEntries.find(
          (r) => r.raw.toLowerCase() === trimmed.toLowerCase(),
        );
        if (exact) onChange({ id: exact.id });
        else if (existing) onChange({ raw: existing.raw });
        else onChange({ raw: trimmed });
      }
    }
    setOpen(false);
    setQ("");
    setUserEdited(false);
  }, [userEdited, q, catalog, rawEntries, onChange]);

  useClickOutside(ref, closeAndAutoCommit, popRef);
  const popPos = usePopoverPosition(triggerRef, open, {
    minWidth: popoverMinWidth,
    align: popoverAlign,
  });

  const selRow = value?.id ? catalog.find((r) => r.id === value.id) : null;
  const selRaw = value?.raw ?? null;

  // Filter + group canonical rows. Filtering kicks in only when the user
  // has actually started typing (userEdited === true).
  const effectiveQ = userEdited ? q.trim() : "";
  const norm = effectiveQ.toLowerCase();
  const filteredCanon = catalog.filter((row) => {
    if (!norm) return true;
    const hay = `${row.code || ""} ${row.label || ""}`.toLowerCase();
    return hay.includes(norm);
  });
  const filteredRaw = rawEntries.filter(
    (r) => !norm || r.raw.toLowerCase().includes(norm),
  );

  type Group = { name: string | null; rows: TypeCatalogRow[] };
  const groups: Group[] = React.useMemo(() => {
    if (!groupBy) return [{ name: null, rows: filteredCanon }];
    const map = new Map<string, TypeCatalogRow[]>();
    for (const row of filteredCanon) {
      const key =
        typeof groupBy === "function" ? groupBy(row) : String(row[groupBy] ?? "—");
      const k = Array.isArray(key) ? (key[0] || "—") : (key || "—");
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(row);
    }
    return [...map.entries()].map(([name, rows]) => ({ name, rows }));
  }, [filteredCanon, groupBy]);

  const exactMatch = filteredCanon.find(
    (r) =>
      (r.code || "").toLowerCase() === norm ||
      (r.label || "").toLowerCase() === norm,
  );
  const exactRaw = filteredRaw.find((r) => r.raw.toLowerCase() === norm);

  const commitId = (id: string) => {
    onChange({ id });
    setOpen(false);
    setQ("");
    setUserEdited(false);
    inputRef.current?.blur();
  };
  const commitRaw = (raw: string) => {
    onChange({ raw });
    setOpen(false);
    setQ("");
    setUserEdited(false);
    inputRef.current?.blur();
  };

  const closedDisplayStr = selRow
    ? closedText
      ? closedText(selRow, value)
      : (selRow.code || selRow.label || "")
    : selRaw
      ? selRaw
      : "";

  const inputValue = open ? q : closedDisplayStr;
  const inputPlaceholder = open ? searchPlaceholder : placeholder;

  // Only use the custom trigger for canonical rows — raw values stay in the
  // input so the typed text stays visible (italic). Otherwise renderTrigger
  // often returns null, leaving the trigger empty.
  const useCustomTrigger = !!(renderTrigger && selRow && !open && !focused);
  const customNode = useCustomTrigger ? renderTrigger!(selRow, value) : null;

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div
        ref={triggerRef}
        onClick={() => inputRef.current?.focus()}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 8px",
          borderRadius: 5,
          border: `1px solid ${open || focused ? "var(--ink)" : "var(--line-strong)"}`,
          background: "var(--panel)",
          boxShadow: open || focused ? "0 0 0 2px var(--accent-soft)" : "none",
          transition: "border-color 120ms, box-shadow 120ms",
          cursor: "text",
          minHeight: 30,
        }}
      >
        {prefix && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {prefix}
          </span>
        )}
        {useCustomTrigger ? (
          <span
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            {customNode}
          </span>
        ) : null}
        <input
          ref={inputRef}
          aria-label={ariaLabel}
          value={inputValue}
          onChange={(e) => {
            setQ(e.target.value);
            setUserEdited(true);
            if (!open) setOpen(true);
          }}
          onFocus={(e) => {
            setFocused(true);
            setOpen(true);
            // Mirror the closed display into the input AND select all so the
            // first keystroke replaces it. Filtering stays off until that
            // first keystroke.
            if (closedDisplayStr) {
              setQ(closedDisplayStr);
              setUserEdited(false);
              const el = e.target;
              window.setTimeout(() => el.select(), 0);
            }
          }}
          onBlur={() => {
            setFocused(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              if (exactMatch) commitId(exactMatch.id);
              else if (exactRaw) commitRaw(exactRaw.raw);
              else if (userEdited && q.trim()) commitRaw(q.trim());
              else if (filteredCanon.length === 1 && filteredCanon[0])
                commitId(filteredCanon[0].id);
            }
            if (e.key === "Escape") {
              setOpen(false);
              setQ("");
              setUserEdited(false);
              inputRef.current?.blur();
            }
          }}
          placeholder={inputPlaceholder}
          style={{
            flex: 1,
            minWidth: 0,
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: 13,
            color: selRaw && !open ? "var(--ink-2)" : "var(--ink)",
            fontStyle: selRaw && !open ? "italic" : "normal",
            fontFamily: "var(--font-mono)",
            fontWeight: 600,
            padding: "1px 2px",
            textAlign: inputAlign,
            cursor: "text",
            position: useCustomTrigger ? "absolute" : "static",
            opacity: useCustomTrigger ? 0 : 1,
            pointerEvents: useCustomTrigger ? "none" : "auto",
            width: useCustomTrigger ? 0 : "auto",
          }}
        />
        <span
          style={{
            color: "var(--ink-3)",
            display: "inline-flex",
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 160ms ease",
            flexShrink: 0,
          }}
        >
          <CaretDown />
        </span>
      </div>

      {open && popPos &&
        createPortal(
          <div
            ref={popRef}
            data-popover-portal="1"
            style={{
              position: "fixed",
              left: popPos.left,
              top: popPos.top,
              bottom: popPos.bottom,
              width: popPos.width,
              zIndex: 1000,
              background: "var(--panel)",
              border: "1px solid var(--line-strong)",
              borderRadius: 8,
              boxShadow: "0 12px 32px -8px rgba(20,30,22,0.22)",
              maxHeight: Math.min(
                popoverMaxHeight,
                Math.max(220, popPos.maxHeight || popoverMaxHeight),
              ),
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <div style={{ flex: 1, overflow: "auto" }}>
              {filteredCanon.length > 0 && (
                <SectionHead
                  title="Standardized"
                  count={filteredCanon.length}
                  kind="canon"
                />
              )}
              {groups.map((g, gi) => (
                <React.Fragment key={gi}>
                  {g.name && groupBy && (
                    <div
                      style={{
                        padding: "5px 12px",
                        background: "var(--bg)",
                        borderBottom: "1px solid var(--line-2)",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        className="mono"
                        style={{
                          fontSize: 9,
                          color: "var(--ink-4)",
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          fontWeight: 600,
                        }}
                      >
                        {g.name}
                      </span>
                    </div>
                  )}
                  {g.rows.map((row) => {
                    const sel = value?.id === row.id;
                    return (
                      <button
                        key={row.id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => commitId(row.id)}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "7px 12px",
                          border: "none",
                          textAlign: "left",
                          background: sel
                            ? "var(--accent-soft)"
                            : "transparent",
                          borderLeft: `3px solid ${sel ? "var(--accent)" : "transparent"}`,
                          cursor: "pointer",
                          color: "inherit",
                        }}
                        onMouseEnter={(e) => {
                          if (!sel)
                            e.currentTarget.style.background = "var(--bg-sunk)";
                        }}
                        onMouseLeave={(e) => {
                          if (!sel)
                            e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          {renderLabel ? (
                            renderLabel(row)
                          ) : (
                            <>
                              <span
                                style={{
                                  fontFamily: "var(--font-mono)",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "var(--ink)",
                                }}
                              >
                                {row.code}
                              </span>
                              <span
                                style={{ fontSize: 12, color: "var(--ink-2)" }}
                              >
                                {row.label}
                              </span>
                            </>
                          )}
                        </span>
                        {renderHint && (
                          <span
                            className="mono"
                            style={{
                              fontSize: 9.5,
                              color: "var(--ink-4)",
                            }}
                          >
                            {renderHint(row)}
                          </span>
                        )}
                        {sel && (
                          <span style={{ color: "var(--accent-ink)" }}>
                            <Check />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </React.Fragment>
              ))}

              {filteredRaw.length > 0 && (
                <>
                  <SectionHead
                    title="Custom"
                    count={filteredRaw.length}
                    kind="raw"
                  />
                  {filteredRaw.map((r, i) => {
                    const sel = value?.raw === r.raw;
                    return (
                      <button
                        key={i}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => commitRaw(r.raw)}
                        style={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "7px 12px",
                          border: "none",
                          textAlign: "left",
                          background: sel
                            ? "var(--signal-soft)"
                            : "transparent",
                          borderLeft: `3px solid ${sel ? "#e7c97a" : "transparent"}`,
                          cursor: "pointer",
                          color: "inherit",
                        }}
                        onMouseEnter={(e) => {
                          if (!sel)
                            e.currentTarget.style.background = "var(--bg-sunk)";
                        }}
                        onMouseLeave={(e) => {
                          if (!sel)
                            e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 12,
                              fontStyle: "italic",
                              color: "var(--ink-2)",
                            }}
                          >
                            "{r.raw}"
                          </span>
                          <RawBadge small />
                        </span>
                        {(r.uses !== undefined || r.addedAgo) && (
                          <span
                            className="mono"
                            style={{
                              fontSize: 9.5,
                              color: "var(--ink-4)",
                            }}
                          >
                            {r.uses ?? "—"}× · {r.addedAgo ?? ""}
                          </span>
                        )}
                        {sel && (
                          <span style={{ color: "#7c5813" }}>
                            <Check />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </>
              )}

              {filteredCanon.length === 0 &&
                filteredRaw.length === 0 &&
                !q.trim() && (
                  <div
                    style={{
                      padding: "16px 14px",
                      color: "var(--ink-4)",
                      fontSize: 12,
                      fontStyle: "italic",
                      textAlign: "center",
                    }}
                  >
                    No matches.
                  </div>
                )}
              {filteredCanon.length === 0 &&
                filteredRaw.length === 0 &&
                q.trim() && (
                  <div
                    style={{
                      padding: "16px 14px",
                      color: "var(--ink-4)",
                      fontSize: 12,
                      lineHeight: 1.5,
                      textAlign: "center",
                    }}
                  >
                    Press{" "}
                    <strong style={{ color: "var(--ink-2)" }}>Enter</strong> to
                    use{" "}
                    <strong
                      style={{
                        fontStyle: "italic",
                        color: "var(--ink-2)",
                      }}
                    >
                      "{q.trim()}"
                    </strong>{" "}
                    as a custom value.
                  </div>
                )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
